import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";

/**
 * Sprint 20a — memory bulk backfill. Backs the Settings → Workspace "Memory
 * backfill" panel: a status query (how many memories are indexed for semantic
 * search) plus a trigger mutation (vectorise the rest). Both call owner-only MCP
 * tools, so the panel is only rendered for owners. The status is cached for 30s
 * — backfill state changes are rare, so polling every render would be wasteful.
 */

export interface MemoryBackfillStatus {
  indexed_count: number;
  pending_count: number;
  total: number;
  in_progress: boolean;
  completed_at: string | null;
  index_bound: boolean;
}

export interface MemoryBackfillResult {
  succeeded: number;
  failed: number;
  failed_memory_ids: string[];
  total_latency_ms: number;
  batches_processed: number;
  total_targeted: number;
}

const STATUS_KEY = ["memory-backfill-status"];

const EMPTY: MemoryBackfillStatus = {
  indexed_count: 0,
  pending_count: 0,
  total: 0,
  in_progress: false,
  completed_at: null,
  index_bound: false,
};

export function useMemoryBackfillStatus(enabled = true) {
  return useQuery<MemoryBackfillStatus>({
    queryKey: STATUS_KEY,
    queryFn: async () => {
      const r = await mcpClient.toolsCall<{ status: MemoryBackfillStatus }>(
        "get_memory_backfill_status",
      );
      if (r.isError) throw new Error(r.content?.[0]?.text ?? "Couldn't load backfill status");
      return r.structuredContent?.status ?? EMPTY;
    },
    enabled,
    staleTime: 30_000,
  });
}

export function useBackfillMemories() {
  const qc = useQueryClient();
  return useMutation<MemoryBackfillResult, Error, void>({
    mutationFn: async () => {
      const r = await mcpClient.toolsCall<{ result: MemoryBackfillResult }>(
        "backfill_workspace_memories",
      );
      if (r.isError) {
        const data = (r.structuredContent ?? {}) as { message?: string; error?: string };
        throw new Error(data.message || data.error || r.content?.[0]?.text || "Backfill failed");
      }
      return r.structuredContent?.result as MemoryBackfillResult;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: STATUS_KEY });
    },
  });
}
